import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

export type RestaurantTable = {
  id: string;
  organizationId: string;
  label: string;
  slug: string;
  qrToken: string;
  active: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type UpsertTableInput = {
  label: string;
  slug?: string;
  displayOrder?: number;
  active?: boolean;
};

export function useTables() {
  return useQuery({
    queryKey: queryKeys.orders.tables(),
    queryFn: async () => await requestJson<{ tables: RestaurantTable[] }>("/api/orders/tables"),
  });
}

export function useCreateTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpsertTableInput) =>
      (
        await requestJson<{ table: RestaurantTable }>("/api/orders/tables", {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify(data),
        })
      ).table,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.tables() });
    },
  });
}

export function useUpdateTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; data: Partial<UpsertTableInput> }) =>
      (
        await requestJson<{ table: RestaurantTable }>(`/api/orders/tables/${input.id}`, {
          method: "PATCH",
          headers: JSON_HEADERS,
          body: JSON.stringify(input.data),
        })
      ).table,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.tables() });
    },
  });
}

export function useDeleteTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      await requestJson<{ success: boolean }>(`/api/orders/tables/${id}`, {
        method: "DELETE",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.tables() });
    },
  });
}

export function useRotateTableQr() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await requestJson<{ table: RestaurantTable }>(`/api/orders/tables/${id}/rotate-qr`, {
          method: "POST",
        })
      ).table,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.tables() });
    },
  });
}
